import './GoogleAdsAudit.scss';
import { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Page } from '@Components/Page';
import { Dropdown } from '@Components/Dropdown';
import { Size } from '@Components/designConstants';
import { useAppSelector } from '../../redux';
import { companiesSelector } from '../../redux/user';
import { AdminSection } from './admin/AdminSection';
import { AccountSearchPanel } from './components/AccountSearchPanel';
import { AccountWorkspace } from './components/AccountWorkspace';
import { CompanyMccPicker } from './components/CompanyMccPicker';
import { CoverageOverview } from './components/CoverageOverview';
import { ManualMccEntry } from './components/ManualMccEntry';
import { GoogleAdsAuditTab, GoogleAdsAuditTabType } from './google-ads-audit-constants';

const NAVS = [
  { key: GoogleAdsAuditTab.SEARCH, label: 'Accounts' },
  { key: GoogleAdsAuditTab.ADMIN, label: 'Admin' }
];

const AccountsTab = () => {
  const companies = useAppSelector(companiesSelector);
  const companyOptions = useMemo(() => companies.map(c => c.company).sort(), [companies]);
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(null);
  const [company, setCompany] = useState<string | null>(null);
  const [mccId, setMccId] = useState<string | null>(null);
  const [manualEntry, setManualEntry] = useState(false);

  const reset = () => {
    setSelectedAccount(null);
    setCompany(null);
    setMccId(null);
    setManualEntry(false);
  };

  if (selectedAccount && company) {
    return (
      <AccountWorkspace
        company={company}
        accountId={selectedAccount.id}
        accountName={selectedAccount.name}
        onBack={reset}
      />
    );
  }

  // Manual-entry path only: the MCC wasn't tied to a company up front, so the account's brand
  // profile (company-owned data) still needs one once an account is picked.
  if (selectedAccount) {
    return (
      <Form.Group className='google-ads-audit-company-picker'>
        <Form.Label>Which company owns &ldquo;{selectedAccount.name}&rdquo;?</Form.Label>
        <Dropdown
          size={Size.LG}
          searchable
          options={companyOptions}
          value={company}
          onChange={setCompany}
          placeholder='Select a company...'
          menuPortalTarget={document.body}
        />
      </Form.Group>
    );
  }

  if (mccId) {
    return (
      <AccountSearchPanel
        mccId={mccId}
        onBack={() => setMccId(null)}
        onSelectAccount={(id, name) => setSelectedAccount({ id, name })}
      />
    );
  }

  if (manualEntry) {
    return <ManualMccEntry onBack={() => setManualEntry(false)} onSelectMcc={setMccId} />;
  }

  return (
    <>
      <CompanyMccPicker
        companyOptions={companyOptions}
        onSelectMcc={(id, selectedCompany) => {
          setCompany(selectedCompany);
          setMccId(id);
        }}
        onSelectAccount={(id, name, selectedCompany) => {
          setCompany(selectedCompany);
          setSelectedAccount({ id, name });
        }}
        onManualEntry={() => setManualEntry(true)}
      />
      <CoverageOverview />
    </>
  );
};

const GoogleAdsAudit = () => {
  const [activeTab, setActiveTab] = useState<GoogleAdsAuditTabType>(GoogleAdsAuditTab.SEARCH);

  return (
    <Page
      pageType='Google Ads Audit'
      title='Google Ads Audit'
      navs={NAVS}
      selectedNav={activeTab}
      onNav={key => setActiveTab(key as GoogleAdsAuditTabType)}
    >
      <div className='google-ads-audit-page'>
        {activeTab === GoogleAdsAuditTab.ADMIN ? <AdminSection /> : <AccountsTab />}
      </div>
    </Page>
  );
};

export { GoogleAdsAudit };
